/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button } from 'primereact/button';
import { Menu as PrimeMenu } from 'primereact/menu';
import { Toolbar } from 'primereact/toolbar';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { APP_MENU, AppMenuItem } from '../menuItems';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import LanguageToggle from '../components/LanguageToggle';
import { Avatar } from 'primereact/avatar';
import { useAuth } from '../contexts/AuthContext';
import { MenuItem } from 'primereact/menuitem';
import { Badge } from 'primereact/badge';
import { MessageTemplateProvider } from '../contexts/MessageTemplateContext';
import { EmailQueueService } from '../services/EmailQueueService';

interface AdminPageProps {
    children: React.ReactNode;
    title: string;
}

const AdminPage: React.FC<AdminPageProps> = ({ children, title }) => {
    const isMdUp = useMediaQuery('(min-width: 1124px)');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const menu = React.useRef<PrimeMenu>(null);
    const { currentUser, logout, isAdmin, roles } = useAuth();
    const avatarMenu = React.useRef<PrimeMenu>(null);
    const [pendingEmailCount, setPendingEmailCount] = React.useState<number>(0);

    React.useEffect(() => {
        if (!isAdmin) return;
        const fetchCount = async () => {
            try {
                const count = await EmailQueueService.pendingCount();
                setPendingEmailCount(count);
            } catch {
                // silently ignore
            }
        };
        fetchCount();
        const interval = setInterval(fetchCount, 60_000);
        return () => clearInterval(interval);
    }, [isAdmin]);

    const menuItems: any[] = APP_MENU.map((m: AppMenuItem) => ({
        id: m.id,
        label: t(m.labelKey),
        icon: m.icon,
        command: () => { navigate(m.path); },
        className: location.pathname === m.path ? 'p-button-outlined p-highlight' : 'p-button-outlined',
        roles: m.roles ?? [],
        template: (item: any, options: any) => (
            <div className="p-menuitem-content" data-pc-section="content">
                <a className={options.className} onClick={options.onClick}
                   style={m.id === 'emailQueue' && pendingEmailCount > 0 ? { justifyContent: 'space-between' } : undefined}>
                    <span className={`p-menuitem-icon ${item.icon}`} data-pc-section="icon"></span>
                    <span className="p-menuitem-text" data-pc-section="label">{item.label}</span>
                    {m.id === 'emailQueue' && pendingEmailCount > 0 && (
                        <Badge value={String(pendingEmailCount)} severity="danger" />
                    )}
                </a>
            </div>
        )
    }));

    // Determine visible menu items based on roles.
    // - If user is admin, show everything.
    // - If a menu item has no required roles (roles: [] or undefined), it's public and shown to everyone.
    // - Otherwise show the item only if user's roles intersect with the item's required roles.
    const userRoles = roles ?? [];
    const visibleMenuItems = isAdmin
        ? menuItems
        : menuItems.filter(item => {
            const required: string[] = (item as any).roles ?? [];
            if (required.length === 0) return true;
            return required.some(r => userRoles.includes(r));
        });

    const avatarMenuItems: MenuItem[] = [
        {
            label: t('menu.logout'),
            icon: 'pi pi-fw pi-sign-out',
            command: () => { logout(); navigate("/login"); }
        }
    ];

    const start = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src={"/" + process.env.REACT_APP_LOGO} alt="logo" style={{ height: '2rem'}} className='mr-3'/>
            {!isMdUp && (
                <>
                    <Button icon="pi pi-bars" onClick={(e) => menu.current?.toggle(e)} />
                    <PrimeMenu model={visibleMenuItems} popup ref={menu} />
                </>
            )}
            <span className="text-xl font-bold ml-3" style={{ color: 'var(--primary-color-text)' }}>{title}</span>
        </div>
    );

    const end = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <LanguageToggle />
            {currentUser && (
                <>
                    <Avatar
                        image={currentUser.photoURL || undefined}
                        shape="circle"
                        className="ml-2"
                        size="normal"
                        onClick={(e) => avatarMenu.current?.toggle(e)}
                    />
                    <PrimeMenu model={avatarMenuItems} popup ref={avatarMenu} />
                </>
            )}
        </div>
    );

    return (
        <MessageTemplateProvider>
            <Toolbar
                start={start}
                end={end}
                className="fixed top-0 left-0 w-full z-5 p-toolbar custom-toolbar"
            />
            <div className="grid mt-8" style={{ padding: '0 2rem' }}>
                {isMdUp && <div className="col-2"><PrimeMenu model={visibleMenuItems} /></div>}
                <div className={isMdUp ? "col-10" : "col-12"}>
                    {children}
                </div>
            </div>
        </MessageTemplateProvider>
    );
};

export default AdminPage;
